/* eslint-disable prettier/prettier */
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { ENV } from '@environment';
import { Formik, FormikValues } from 'formik';
import GoogleMapReact from 'google-map-react';
import { MyThunkDispatch, RootState } from 'store';
import * as yup from 'yup';

import { addressesEdit } from 'action-creators/addresses';
import Header from 'components/AppBar';
import SubmitButton from 'components/Buttons/SubmitButton';
import Input from 'components/Input';
import { Desktop, Mobile } from 'components/Responsive';
import ScreenHeader from 'components/ScreenHeader';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import { CompanyAddressRequest } from 'models/company';
import { fixCity, getCityCode, loadCities } from 'services/allCities';
import { saveCompanyAddress } from 'services/companyService';
import { extractErrorMessage } from 'utils/generic';
import { getCountryCode, GoogleMapsWrapper, getCity, getCountry } from 'utils/googlemaps';

import InputAddress from './components/InputAddress';
import { Container, Content, Wrapper, FormContainer, Title, Field, MapBox, Address, ButtonContainer } from './styles';

interface ICompanyBillingMapForm {
  description: string;
}

const markers: any = [];
let gmapsWrapper: GoogleMapsWrapper;

const CompanyBillingMapForm = () => {
  const GOOGLE_MAPS_API_KEY = `${ENV.API_KEYS.GOOGLE_MAPS_API_KEY}`;
  const DEFAULT_ZOOM_VALUE = 16;
  const history = useHistory();
  const { t } = useStoreTranslation();
  const toast = useToast();
  const dispatch: MyThunkDispatch = useDispatch();
  const { company } = useSelector((state: RootState) => state.companyBilling);
  const { selected } = useSelector((state: RootState) => state.addresses);
  const [mapCenter, setMapCenter] = useState<google.maps.LatLngLiteral>();

  const [loading, setLoading] = useState<boolean>(false);

  const [gMapsReady, setGMapsReady] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [addressFound, setAddressFound] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [countryId, setCountryId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadedGoogleApi = (mapObj, mapsObj) => {
    gmapsWrapper = new GoogleMapsWrapper(mapObj, mapsObj);
    setGMapsReady(true);
  };

  const setData = useCallback(
    async (isCancelled) => {
      if (selected && selected.place_id && !isCancelled && gMapsReady && mapCenter === undefined) {
        const res = await gmapsWrapper.geocodePlaceId(selected.place_id);
        const { location } = res[0].geometry;
        const lat = location.lat();
        const lng = location.lng();
        setMapCenter({ lat, lng });
        setCity(getCity(res[0].address_components));
        setCountry(getCountry(res[0].address_components));
        setCountryId(getCountryCode(res[0].address_components));

        gmapsWrapper.moveToLocation(lat, lng);
        setShowMap(true);

        setAddressFound(selected?.description || '');
        dispatch(addressesEdit(selected?.description || ''));
      }
    },
    [dispatch, gMapsReady, mapCenter, selected],
  );

  useEffect(() => {
    let isCancelled = false;
    setData(isCancelled);

    return () => {
      isCancelled = true;
    };
  }, [setData]);

  const handleSubmit = useCallback(
    async (values: ICompanyBillingMapForm) => {
      setIsSubmitting(true);
      setLoading(true);

      let cityCode = '';
      if (city !== '') {
        const cityFixed = fixCity(city);

        const cities = [
          ...(await loadCities(city, country, 'es')),
          ...(await loadCities(city, country, 'pt')),
          ...(await loadCities(city, country, 'en')),
        ];
        cityCode = getCityCode(cities, cityFixed, country);
        if (cityCode === '') {
          toast.add({
            type: 'danger',
            message: t('billing.map.error.cityNotFound'),
          });
          setLoading(false);
          setIsSubmitting(false);
          return;
        }
      }
      if (countryId && mapCenter) {
        if (countryId !== company?.countryId) {
          toast.add({
            type: 'danger',
            message: t('billing.map.error.differentCountry', { country: t(`countries.${company?.countryId}`) }),
          });
          setIsSubmitting(false);
          return;
        }

        const address = addressFound;
        const lat = mapCenter.lat;
        const lng = mapCenter.lng;

        if (address !== '' && lat && lng) {
          const addressForm: CompanyAddressRequest = {
            address,
            description: values.description,
            lat,
            lng,
            city_code: cityCode,
          };

          try {
            await saveCompanyAddress(addressForm);
            history.push('/companyBilling', history.location.state);
          } catch (e) {
            toast.add({
              type: 'danger',
              message: e.error || e.message || extractErrorMessage(e),
            });
            setLoading(false);
          } finally {
            setIsSubmitting(false);
          }
        }
      }
    },
    [addressFound, city, company?.countryId, country, countryId, history, mapCenter, t, toast],
  );

  const validationSchema = yup.object().shape({
    description: yup.string().trim().max(100),
  });

  const initValues = {
    description: company?.address?.description || '',
  };

  const handleBack = () => {
    history.push('/companyBillingAddressEdit', history.location.state);
  };

  const formatAddress = (
    address_components: google.maps.GeocoderAddressComponent[],
    formatted_address: string,
  ): string => {
    let isBR = false;
    const address: any = {};
    address_components.forEach((item) => {
      address[item.types[0]] = item.short_name;

      if (item.types[0] === 'country') {
        address[item.types[0]] = item.long_name;
        isBR = item.short_name === 'BR';
      }
    });

    if (!isBR) {
      return formatted_address;
    }

    let formatted = address.route ? `${address.route}` : '';
    if (address.street_number && `${address.street_number}` !== '') formatted += `, ${address.street_number}`;
    if (address.political && `${address.political}` !== '') formatted += ` - ${address.political}`;
    if (address.administrative_area_level_2 && `${address.administrative_area_level_2}` !== '')
      formatted += `, ${address.administrative_area_level_2}`;
    if (address.administrative_area_level_1 && `${address.administrative_area_level_1}` !== '')
      formatted += ` - ${address.administrative_area_level_1}`;
    if (address.postal_code && `${address.postal_code}` !== '') formatted += `, ${address.postal_code}`;
    if (address.country && `${address.country}` !== '') formatted += `, ${address.country}`;
    return formatted;
  };

  const updateAddress = useCallback(
    async (center, bounds: any) => {
      if (gMapsReady) {
        try {
          const resp = await gmapsWrapper.geocode(center);

          if (resp && resp.length > 0) {
            const test: any = resp.find((r) => r.place_id === selected?.place_id) || resp[0];
            const formatted_address = formatAddress(test.address_components, test.formatted_address);

            setAddressFound(formatted_address);
            dispatch(addressesEdit(formatted_address));
            const city = getCity(test.address_components);
            if (city === '') {
              toast.add({
                type: 'danger',
                message: t('billing.map.error.cityNotFound'),
              });
            }
            setCity(city);
            setCountry(getCountry(test.address_components));
            setCountryId(getCountryCode(test.address_components));
          }
        } catch (e) { }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, gMapsReady, t, toast],
  );

  const removeAllMarkers = () => {
    const markersArr = markers;
    for (let i = 0; i < markersArr.length; i++) {
      markers[i].setMap(null);
    }
    markers.splice(0);
  };

  useEffect(() => {
    if (gMapsReady) {
      const init = async () => {
        removeAllMarkers();
        if (gmapsWrapper && mapCenter) {
          const added: any = await gmapsWrapper.addMarker(mapCenter, (center, upBounds) => { });
          markers.push(added);
        }
      };
      init();
    }
  }, [gMapsReady, mapCenter, updateAddress]);

  return (
    <Wrapper className="target">
      <Container>
        <Desktop>
          <ScreenHeader title={t('billing.profile.title')} />
        </Desktop>

        <Mobile>
          <Header goBack={handleBack}>{t('billing.profile.title')}</Header>
        </Mobile>

        <Content>
          <Title>{t('billing.map.title')}</Title>
          <MapBox>
            <div style={showMap ? { width: '100%', height: '100%' } : { width: 0, height: 0 }}>
              {
                <GoogleMapReact
                  bootstrapURLKeys={{
                    key: GOOGLE_MAPS_API_KEY,
                    language: t('locale').substring(0, 2),
                    region: t('locale').substring(3),
                    libraries: ['places'],
                  }}
                  defaultCenter={mapCenter ? mapCenter : { lat: 0, lng: 0 }}
                  defaultZoom={DEFAULT_ZOOM_VALUE}
                  yesIWantToUseGoogleMapApiInternals
                  onDragEnd={async ({ center }) => {
                    if (!gMapsReady) return;
                    if (!gmapsWrapper) return;

                    updateAddress(center, gmapsWrapper.getUpBounds());
                    markers[0].setPosition(center);
                  }}
                  onDrag={async ({ center }) => {
                    markers[0].setPosition(center);
                  }}
                  options={{
                    fullscreenControl: false,
                    zoomControl: false,
                  }}
                  onGoogleApiLoaded={({ map, maps }) => loadedGoogleApi(map, maps)}
                />
              }
            </div>
            <div id="mapbox"></div>
          </MapBox>
          <Address>
            <Address.Title>{t('billing.map.address')}</Address.Title>
            <InputAddress
              value={addressFound}
              placeholder={t('billing.map.addressPlaceholder')}
              onClick={() => {
                history.push('/companyBillingAddressEdit', history.location.state);
              }}
            />
          </Address>
          <Formik
            initialStatus={{ rightCheckIfIsValid: true }}
            initialValues={initValues}
            validationSchema={validationSchema}
            onSubmit={(value) => handleSubmit(value as ICompanyBillingMapForm)}
            isInitialValid={(formik: FormikValues) => validationSchema.isValidSync(formik.initialValues)}
          >
            {({ dirty }: FormikValues) => (
              <>
                <FormContainer $isDirty={dirty}>
                  <Field name="description" label={t('billing.map.description')}>
                    <Input id="description" placeholder={t('billing.map.descriptionPlaceholder')} />
                  </Field>
                  <ButtonContainer>
                    <SubmitButton isEdit={false} dirty={dirty} loading={loading || isSubmitting}>
                      {t('billing.map.buttonConfirm')}
                    </SubmitButton>
                  </ButtonContainer>
                </FormContainer>
              </>
            )}
          </Formik>
        </Content>
      </Container>
    </Wrapper>
  );
};

export default CompanyBillingMapForm;
